import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import { formatDate } from "../../../utils/formatDate";

// Types

/** Approval status for each section (Basic Info, Diagnosis, Procedure). */
export type ApprovalStatus = "valid" | "invalid" | null;

export interface ProcedureFieldState {
  cptCode: string;
  drugName: string;
  genericName: string;
  dosage: string;
  unitsApproved: string;
  numVisits: string;
  startDate: string;
  endDate: string;
  authStatus: string;
}

/** Editable basic information section state. */
export interface BasicInfoFieldState {
  primaryInsurance: string;
  memberId: string;
  providerName: string;
  authorizationId: string;
  authInitiatedDate: string;
  sourcePortal: string;
}

/** Single diagnosis row state for Diagnosis Detail section. */
export interface DiagnosisDetailItemState {
  diagnosisCode: string;
  diagnosisInformation: string;
}

// Page & section titles

export const AUTHORISATION_DETAILS_TITLE = "Authorisation Details";
export const BASIC_INFO_TITLE = "Basic Information";
export const SOURCE_PORTAL_LABEL = "Source Portal";
export const PATIENT_NAME_LABEL = "Patient Name";
export const EXTRACTED_PAYER_NAME_LABEL = "Payer Name";
export const DIAGNOSIS_DETAIL_TITLE = "Diagnosis Detail";
export const PROCEDURE_INFO_TITLE = "Procedure Information";

// Button & action labels

export const CANCEL_BUTTON_TEXT = "Cancel";
export const SAVE_BUTTON_TEXT = "Save";
export const LOADING_AGENT_DIFF_MESSAGE = "Loading...";
export const LOADING_AGENT_RERUN_MESSAGE = "Extracting letter details...";
export const APPROVE_LABEL = "Approve";
export const REJECT_LABEL = "Reject";
export const INCORRECT_LETTER_LABEL = "Incorrect Letter";
export const DOWNLOAD_LABEL = "Download";
export const DOCUMENTS_LABEL = "Documents";

// Approval / feedback labels & colors

export const VALID_BADGE_LABEL = "Valid";
export const INVALID_BADGE_LABEL = "Invalid";
export const FEEDBACK_MODE_BADGE_LABEL = "Feedback Mode - Fix Incorrect Fields";
export const VALUE_CHANGED_LABEL = "Value Changed";
export const VALUE_CHANGED_BG = "#FFF3E0";
export const VALUE_CHANGED_TEXT = "#C24400";

export const APPROVAL_VALID_BG = "#E6F3F0";
export const APPROVAL_VALID_TEXT = "#005D49";
export const APPROVAL_INVALID_BG = "#FFE8E8";
export const APPROVAL_INVALID_TEXT = "#CC0300";

// Placeholder

export const PLACEHOLDER = "—";

// Basic Information field config

export const BASIC_INFO_FIELDS: {
  label: string;
  getValue: (jCode: JCodeDetails) => string;
}[] = [
  {
    label: "Primary Insurance",
    getValue: (jCode) => jCode.auth_letter?.primary_insurance ?? PLACEHOLDER,
  },
  {
    label: "Member ID",
    getValue: (jCode) => jCode.auth_letter?.member_id ?? PLACEHOLDER,
  },
  {
    label: "Provider Name",
    getValue: (jCode) => jCode.auth_letter?.provider ?? PLACEHOLDER,
  },
  {
    label: "Authorization ID",
    getValue: (jCode) =>
      jCode.auth_letter?.auth_letter_auth_number ?? PLACEHOLDER,
  },
  {
    label: "Auth Initiated Date",
    getValue: (jCode) =>
      formatDate(
        jCode.auth_letter?.auth_letter_start_date,
        "MM-DD-YYYY",
        PLACEHOLDER,
      ),
  },
];

export const BASIC_INFO_FIELD_CONFIGS: {
  key: keyof BasicInfoFieldState;
  label: string;
  placeholder: string;
}[] = [
  {
    key: "primaryInsurance",
    label: "Primary Insurance",
    placeholder: "Primary Insurance",
  },
  { key: "memberId", label: "Member ID", placeholder: "Member ID" },
  { key: "providerName", label: "Provider Name", placeholder: "Provider Name" },
  {
    key: "authorizationId",
    label: "Authorization ID",
    placeholder: "Authorization ID",
  },
  {
    key: "authInitiatedDate",
    label: "Auth Initiated Date",
    placeholder: "MM-DD-YYYY",
  },
];

/**
 * Basic Information fields that must hold a valid value before Save is
 * allowed. Single source of truth for the required-field validator and the
 * red asterisk rendering (`sourcePortal` is rendered outside the config loop).
 */
export const REQUIRED_BASIC_INFO_FIELDS: ReadonlyArray<
  keyof BasicInfoFieldState
> = ["authorizationId", "sourcePortal"];

// Procedure Information field config

export const PROCEDURE_FIELD_CONFIGS: {
  key: keyof ProcedureFieldState;
  label: string;
  placeholder: string;
  fullWidth?: boolean;
  showStatusDot?: boolean;
}[] = [
  { key: "cptCode", label: "CPT Code", placeholder: "CPT Code" },
  { key: "drugName", label: "Drug Name", placeholder: "Drug Name" },
  { key: "genericName", label: "Generic Name", placeholder: "Generic Name" },
  { key: "dosage", label: "Dosage", placeholder: "Dosage" },
  {
    key: "unitsApproved",
    label: "Units Approved",
    placeholder: "Units Approved",
  },
  {
    key: "numVisits",
    label: "Num Visits",
    placeholder: "Num Visits",
  },
  { key: "startDate", label: "Start Date", placeholder: "Start Date" },
  { key: "endDate", label: "End Date", placeholder: "End Date" },
  {
    key: "authStatus",
    label: "Auth Status",
    placeholder: "Auth Status",
    showStatusDot: true,
  },
];

/**
 * Procedure fields (checked per J-code row) that must hold a valid value
 * before Save is allowed. Single source of truth for the required-field
 * validator and the red asterisk rendering. `numVisits` is a count.
 */
export const REQUIRED_PROCEDURE_FIELDS: ReadonlyArray<
  keyof ProcedureFieldState
> = ["numVisits", "startDate", "endDate", "authStatus"];

/** Procedure field key that holds a count (validated as a positive integer). */
export const NUM_VISITS_FIELD_KEY: keyof ProcedureFieldState = "numVisits";
